import { Component, DestroyRef, HostBinding, Inject, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, tap } from 'rxjs';

import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { selectIsLinearBetslip } from '../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipV2OptionMarketPick } from '../../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2WinParticipantPick } from '../../../../core/picks/betslip-v2-win-participant-pick';
import { PickOddsState, PriceType } from '../../../../core/picks/pick-models';
import { BetslipV2HorseRaceOptionMarketPick } from '../../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { EditBetActions } from '../../../edit-bet/actions';
import { betslipTypeStateSelector } from '../../../types/selectors';
import BetslipPickActions from '../../actions';
import { betslipPicksListSelector } from '../../selectors';

@Component({
    selector: 'bs-starting-price-button',
    templateUrl: './starting-price-button.component.html',
    styleUrls: ['./starting-price-button.component.scss'],
    standalone: false,
})
export class StartingPriceButtonComponent implements OnInit {
    @Input() pick: BetslipV2WinParticipantPick | BetslipV2HorseRaceOptionMarketPick;
    @Input() betslipType?: BetslipType;

    @HostBinding('class.deselected')
    get isPickDeselected(): boolean {
        return !this._isPickSelected && !this._isPickAutoSelected;
    }

    messages: IBetslipMessages;
    private _isPickSelected = false;
    private _isPickAutoSelected = false;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private store: Store<IBetslipRootState>,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
    ) {
        this.messages = messageProvider.getMessages();
    }

    ngOnInit(): void {
        const type$ = this.store.select(betslipTypeStateSelector);
        const isLinear$ = this.store.select(selectIsLinearBetslip);

        combineLatest([isLinear$, type$])
            .pipe(
                concatLatestFrom(() => [this.store.select(betslipPicksListSelector)]),
                filter(([_, pickState]) => !!this.pick && pickState.some((p) => p.id.isEqual(this.pick.id))),
                tap(([[isLinear, typeState]]) => {
                    this._isPickSelected = false;

                    const betslipType = this.betslipType || typeState.base.currentSelectedType;

                    switch (betslipType) {
                        case BetslipType.Single:
                            this._isPickSelected = !!typeState.singleBet.picks[this.pick.id.toString()].isSelected;
                            break;
                        case BetslipType.Combo:
                            const multiPick = typeState.comboBet.picks[this.pick.id.toString()];

                            this._isPickSelected = multiPick?.isSelected && (!isLinear || !multiPick?.isLocked);
                            break;
                        case BetslipType.System:
                            const systemPick = typeState.systemBet.picks[this.pick.id.toString()];

                            this._isPickSelected = systemPick?.isSelected && (!isLinear || !systemPick?.isLocked);
                            break;
                        default:
                            break;
                    }
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.store
            .select(betslipTypeStateSelector)
            .pipe(
                map((typeState) => (this._isPickAutoSelected = typeState.base.currentSelectedType === BetslipType.EditBet)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    selectedPriceClick(event: MouseEvent) {
        event.stopPropagation();

        if ((this._isPickSelected || this._isPickAutoSelected) && this.isChangeOddsAvailable() && this.pick.oddsState !== PickOddsState.Locked) {
            if (this.pick instanceof BetslipV2OptionMarketPick && this.pick.siblingOption!) {
                this.store.dispatch(BetslipPickActions.toggleWinPick({ pickId: this.pick.id }));
            } else this.toggleSelectedPrice();
        }
    }

    get showStartingPrice(): boolean {
        return this.pick.isStartingPriceAvailable();
    }

    get startingPriceDisabled(): boolean {
        return this.isStartingPriceSelected && !this.isFixedPriceAvailable;
    }

    get isFixedPriceAvailable(): boolean {
        return this.pick.isFixedPriceAvailable();
    }

    get isStartingPriceSelected(): boolean {
        return !!this.pick.currentPrice && this.pick.currentPrice.type === PriceType.StartingPrice;
    }

    toggleSelectedPrice(): void {
        if (this.isChangeOddsAvailable()) {
            this.store.dispatch(
                this.pick.priceType === PriceType.Fixed
                    ? BetslipPickActions.setStartingPrice({ pickId: this.pick.id })
                    : BetslipPickActions.setFixedPrice({ pickId: this.pick.id }),
            );
            this.store.dispatch(
                this.pick.priceType === PriceType.Fixed
                    ? EditBetActions.setStartingPrice({ pickId: this.pick.id })
                    : EditBetActions.setFixedPrice({ pickId: this.pick.id }),
            );
        }
    }

    private isChangeOddsAvailable(): boolean {
        return (
            (this.pick.priceType === PriceType.Fixed && this.pick.isStartingPriceAvailable()) ||
            (this.pick.priceType === PriceType.StartingPrice && this.pick.isFixedPriceAvailable())
        );
    }
}
